'use client';

import type { JSX } from 'react';
import { useState } from 'react';

interface FaqItem {
  question: string;
  answer: string;
  column: 'left' | 'right';
  defaultOpen?: boolean;
}

interface FaqContent {
  heading: string;
  items: FaqItem[];
}

interface FaqSectionProps {
  content?: Partial<FaqContent>;
}

const DEFAULT_CONTENT: FaqContent = {
  heading: 'Pytania o obsługę prawną firm',
  items: [
    {
      question: 'Czy wspólnik dowie się, że szukam prawnika?',
      answer: 'Nie. Wszystko co napiszesz w chacie jest objęte tajemnicą. Żadne informacje nie trafiają do wspólnika ani nikogo innego. Najpierw poznajesz opcje, potem decydujesz co robić.',
      column: 'left',
      defaultOpen: true,
    },
    {
      question: 'Ile kosztuje wycena?',
      answer: 'Wycena jest całkowicie bezpłatna. Otrzymasz szczegółowy raport z analizą Twojej sprawy bez żadnych zobowiązań.',
      column: 'left',
    },
    {
      question: 'Jak długo trwa przygotowanie raportu?',
      answer: 'Raport otrzymasz w ciągu 24 godzin od zakończenia rozmowy z naszym asystentem.',
      column: 'right',
    },
    {
      question: 'Czy muszę się do czegoś zobowiązywać?',
      answer: 'Nie, wycena nie zobowiązuje Cię do niczego. To Ty decydujesz, czy chcesz skorzystać z naszych usług.',
      column: 'right',
    },
  ],
};

function PlusIcon(): JSX.Element {
  return (
    <div className="w-6 h-6 rounded-full bg-lawmingo-yellow flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5V19M5 12H19" stroke="#242423" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function MinusIcon(): JSX.Element {
  return (
    <div className="w-6 h-6 rounded-full bg-lawmingo-charcoal flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 12H19" stroke="#F5CB5C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

interface FaqItemComponentProps {
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
}

function FaqItemComponent({ item, index, isOpen, onToggle }: FaqItemComponentProps): JSX.Element {
  const closedStyle = {
    backgroundColor: '#CFDBD5',
  };

  const openStyle = {
    background: 'radial-gradient(245.57% 88.83% at 100% 100%, #F8D77D 0%, rgba(248, 215, 125, 0) 100%), #CFDBD5',
  };

  return (
    <div
      className="rounded-card overflow-hidden cursor-pointer transition-all duration-300"
      style={isOpen ? openStyle : closedStyle}
      onClick={() => onToggle(index)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle(index);
        }
      }}
      aria-expanded={isOpen}
    >
      <div className="px-6 py-5 flex items-start justify-between gap-4">
        <h3 className="font-fraunces text-base lg:text-lg text-lawmingo-charcoal leading-snug">
          {item.question}
        </h3>
        <div className="flex-shrink-0 mt-1">
          {isOpen ? <MinusIcon /> : <PlusIcon />}
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-5">
          <p className="font-dmsans text-sm lg:text-base text-lawmingo-charcoal/80 leading-relaxed">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FaqSection({ content }: FaqSectionProps): JSX.Element {
  const sectionContent: FaqContent = { ...DEFAULT_CONTENT, ...content };
  const { heading, items } = sectionContent;

  // Find the initially open item
  const defaultOpenIndex = items.findIndex(item => item.defaultOpen);
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpenIndex !== -1 ? defaultOpenIndex : null);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Split items into left and right columns
  const leftItems = items.filter(item => item.column === 'left');
  const rightItems = items.filter(item => item.column === 'right');

  // Get original index for each item
  const getOriginalIndex = (item: FaqItem): number => items.indexOf(item);

  return (
    <section className="bg-[#F8F4E3] w-full py-[100px]">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
        <h2
          className="font-fraunces text-3xl md:text-4xl lg:text-5xl text-lawmingo-charcoal font-normal mb-12"
          style={{ lineHeight: '1.2' }}
          dangerouslySetInnerHTML={{ __html: heading }}
        />

        {/* Desktop: Two columns */}
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6">
          {/* Left column */}
          <div className="space-y-4">
            {leftItems.map((item) => {
              const originalIndex = getOriginalIndex(item);
              return (
                <FaqItemComponent
                  key={originalIndex}
                  item={item}
                  index={originalIndex}
                  isOpen={openIndex === originalIndex}
                  onToggle={handleToggle}
                />
              );
            })}
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {rightItems.map((item) => {
              const originalIndex = getOriginalIndex(item);
              return (
                <FaqItemComponent
                  key={originalIndex}
                  item={item}
                  index={originalIndex}
                  isOpen={openIndex === originalIndex}
                  onToggle={handleToggle}
                />
              );
            })}
          </div>
        </div>

        {/* Mobile: Single column */}
        <div className="lg:hidden space-y-4">
          {items.map((item, index) => (
            <FaqItemComponent
              key={index}
              item={item}
              index={index}
              isOpen={openIndex === index}
              onToggle={handleToggle}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
